import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Instagram, Facebook, Youtube, Mail, Phone, MapPin } from "lucide-react";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-[#050505] border-t border-white/5">
      {/* Main Footer */}
      <div className="container-custom py-20">
        {/* Large Brand Name */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <h2 className="font-serif text-6xl md:text-8xl lg:text-9xl font-medium text-white/10 tracking-tight">
            Godara Studios
          </h2>
        </motion.div>

        {/* Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* About */}
          <div>
            <h3 className="text-sm uppercase tracking-widest text-gold font-semibold mb-6">About</h3>
            <p className="text-white/60 leading-relaxed text-sm">
              Godara Studios is a premium photography and videography studio based in Punjab, 
              capturing weddings, portraits, and commercial stories since 2000 across Punjab, 
              Haryana, and Rajasthan.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm uppercase tracking-widest text-gold font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-4">
              {["Home", "About", "Services", "Portfolio", "Videos", "Contact"].map((link) => (
                <li key={link}>
                  <Link
                    to={link === "Home" ? "/" : `/${link.toLowerCase()}`}
                    data-testid={`footer-link-${link.toLowerCase()}`}
                    className="text-white/60 hover:text-gold transition-colors duration-300 text-sm"
                  >
                    {link}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-sm uppercase tracking-widest text-gold font-semibold mb-6">Services</h3>
            <ul className="space-y-4">
              {["Wedding Photography", "Pre-Wedding Shoots", "Portrait Photography", "Event Coverage", "Videography"].map((service) => (
                <li key={service}>
                  <span className="text-white/60 text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-sm uppercase tracking-widest text-gold font-semibold mb-6">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-white/60 text-sm">
                <Phone size={16} strokeWidth={1.5} className="text-gold" />
                <span>+91 9417589900</span>
              </li>
              <li className="flex items-center gap-3 text-white/60 text-sm">
                <Mail size={16} strokeWidth={1.5} className="text-gold" />
                <span>godarastudios@gmail.com</span>
              </li>
              <li className="flex items-start gap-3 text-white/60 text-sm">
                <MapPin size={16} strokeWidth={1.5} className="text-gold mt-1" />
                <span>India</span>
              </li>
            </ul>

            {/* Social Links */}
            <div className="flex items-center gap-4 mt-8">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="w-10 h-10 border border-white/10 flex items-center justify-center text-white/60 hover:text-gold hover:border-gold transition-all duration-300"
              >
                <Instagram size={18} strokeWidth={1.5} />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="w-10 h-10 border border-white/10 flex items-center justify-center text-white/60 hover:text-gold hover:border-gold transition-all duration-300"
              >
                <Facebook size={18} strokeWidth={1.5} />
              </a>
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-youtube"
                className="w-10 h-10 border border-white/10 flex items-center justify-center text-white/60 hover:text-gold hover:border-gold transition-all duration-300"
              >
                <Youtube size={18} strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="container-custom py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-xs">
            © {currentYear} Godara Studios. All rights reserved.
          </p>
          <p className="text-white/40 text-xs">
            Crafting Timeless Memories
          </p>
        </div>
      </div>
    </footer>
  );
}
